import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const RegulationsSection = () => {
  const [activeCategory, setActiveCategory] = useState('international');

  const categories = [
    { id: 'international', label: 'International Transfers', icon: 'Globe' },
    { id: 'youth', label: 'Youth Protection', icon: 'Users' },
    { id: 'work', label: 'Work Permits', icon: 'FileText' },
    { id: 'financial', label: 'Financial Regulations', icon: 'DollarSign' }
  ];

  const regulations = {
    international: [
      {
        id: 1,
        title: "FIFA RSTP Article 5 - International Transfers",
        description: "International transfers of players are only permitted during registration periods fixed by the relevant association. Players may only be registered during two registration periods during the year.",
        requirements: [
          "Transfer window compliance for all international moves",
          "International Transfer Certificate (ITC) mandatory",
          "Registration with destination association within 30 days",
          "Solidarity mechanism payments to training clubs"
        ],
        icon: "FileCheck",
        color: "primary"
      },
      {
        id: 2,
        title: "FIFA RSTP Article 18 - Transfer Matching System",
        description: "All international transfers must be registered through FIFA's Transfer Matching System (TMS) to ensure transparency and compliance with regulations.",
        requirements: [
          "TMS registration within 7 days of agreement",
          "Complete documentation upload to platform",
          "Verification of all parties involved",
          "Compliance with solidarity and training compensation"
        ],
        icon: "Database",
        color: "secondary"
      },
      {
        id: 3,
        title: "FIFA RSTP Article 19 - Training Compensation",
        description: "Training compensation is payable to a player's training club(s) when a player signs their first professional contract and on each transfer until the end of the season of their 23rd birthday.",
        requirements: [
          "Calculation based on training costs of new club\'s category",
          "Payment to all clubs that trained player aged 12-21",
          "Pro-rata distribution for partial training periods",
          "Documentation of player\'s training history"
        ],
        icon: "GraduationCap",
        color: "accent"
      }
    ],
    youth: [
      {
        id: 4,
        title: "FIFA RSTP Article 19 - Protection of Minors",
        description: "International transfers of players under 18 are prohibited unless specific exceptions apply. This regulation protects young players from exploitation.",
        requirements: [
          "Parents must relocate for non-football reasons",
          "Transfer within EU/EEA for players aged 16-18",
          "Player lives within 50km of border and club within 50km",
          "FIFA approval required for all minor transfers"
        ],
        icon: "Shield",
        color: "success"
      },
      {
        id: 5,
        title: "FIFA Guardian Program Compliance",
        description: "Clubs must provide adequate care, education, and living conditions for young players, ensuring their welfare and development beyond football.",
        requirements: [
          "Academic education arrangements mandatory",
          "Suitable accommodation and supervision",
          "Regular welfare monitoring and reporting",
          "Return travel provisions if contract terminated"
        ],
        icon: "Heart",
        color: "secondary"
      }
    ],
    work: [
      {
        id: 6,
        title: "UK Work Permit - Governing Body Endorsement",
        description: "Non-UK/Irish players require a Governing Body Endorsement (GBE) from the FA to obtain a work permit. Points-based system determines eligibility.",
        requirements: [
          "International appearances and FIFA ranking points",
          "Club competition participation at required level",
          "Domestic league minutes and quality assessment",
          "Minimum salary threshold compliance"
        ],
        icon: "Briefcase",
        color: "primary"
      },
      {
        id: 7,
        title: "EU Blue Card & Schengen Visa Requirements",
        description: "Players moving to EU countries may require Blue Card or work visa depending on nationality and destination country regulations.",
        requirements: [
          "Valid employment contract with minimum salary",
          "Professional qualifications recognition",
          "Health insurance coverage proof",
          "Criminal background clearance"
        ],
        icon: "MapPin",
        color: "accent"
      }
    ],
    financial: [
      {
        id: 8,
        title: "UEFA Financial Fair Play Regulations",
        description: "Clubs must balance football-related expenditure with revenue, ensuring financial sustainability and preventing excessive debt accumulation.",
        requirements: [
          "Break-even requirement over monitoring period",
          "Acceptable deviation limits compliance",
          "Overdue payables restrictions",
          "Squad cost ratio monitoring"
        ],
        icon: "TrendingUp",
        color: "success"
      },
      {
        id: 9,
        title: "Third-Party Ownership Prohibition",
        description: "FIFA prohibits third-party ownership of players\' economic rights to prevent conflicts of interest and protect player welfare.",
        requirements: [
          "No third-party influence on transfers",
          "Full disclosure of all agreements",
          "Player rights exclusively with clubs",
          "Transparency in payment structures"
        ],
        icon: "Ban",
        color: "error"
      }
    ]
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4 md:mb-6">
            <Icon name="BookOpen" size={20} color="var(--color-primary)" />
            <span className="text-sm md:text-base font-medium text-primary">Regulatory Framework</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6 font-headline">
            FIFA & International Regulations
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Comprehensive overview of key regulations governing international football transfers, player protection, and financial compliance.
          </p>
        </div>

        <div className="mb-8 md:mb-10 lg:mb-12">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setActiveCategory(category?.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  activeCategory === category?.id
                    ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                    : 'bg-card text-foreground border border-border hover:border-primary hover:bg-muted'
                }`}
              >
                <Icon name={category?.icon} size={20} />
                <span className="text-sm md:text-base">{category?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6 md:space-y-8">
          {regulations?.[activeCategory]?.map((regulation) => (
            <div
              key={regulation?.id}
              className="bg-card rounded-xl border border-border p-6 md:p-8 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start gap-4 md:gap-6 mb-6">
                <div className={`w-12 h-12 md:w-14 md:h-14 bg-${regulation?.color}/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon name={regulation?.icon} size={24} color={`var(--color-${regulation?.color})`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground mb-3 font-headline">
                    {regulation?.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {regulation?.description}
                  </p>
                </div>
              </div>

              <div className="pl-0 md:pl-20">
                <h4 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                  Key Requirements
                </h4>
                <ul className="space-y-3">
                  {regulation?.requirements?.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm md:text-base text-foreground">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 md:mt-12 lg:mt-16 bg-muted rounded-xl p-6 md:p-8 lg:p-10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="AlertCircle" size={32} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3 font-headline">
                Regulatory Updates & Changes
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                Football regulations evolve regularly. ProX11Market maintains continuous monitoring of FIFA, UEFA, and national association updates to ensure all operations remain compliant with the latest requirements.
              </p>
              <div className="flex items-center gap-2 text-sm text-primary">
                <Icon name="Clock" size={16} />
                <span>Last updated: January 27, 2026</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegulationsSection;